// "Ask a Question" composer — opens from the Forum index.
//
// Two-step flow:
//   1. user types title, body, picks category
//   2. on submit, if the server flags `looks_like_deal: True`, we show a
//      soft "Did you mean to post on the Deal Wall?" nudge with a Cancel
//      button. We don't block — false positives are annoying — but we
//      strongly steer.
//
// Posts a redirect to the new post detail on success.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MarkdownEditor from "@/components/forum/MarkdownEditor";
import { metaFor } from "@/lib/forumCategories";
import { AlertTriangle, Link as LinkIcon } from "lucide-react";

export default function AskQuestionModal({ open, onOpenChange, categories, defaultCategory }) {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState(defaultCategory || "");
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setBody("");
      setCategory(defaultCategory || "");
      setTagsInput("");
    }
  }, [open, defaultCategory]);

  const canSubmit = title.trim().length >= 8 && body.trim().length >= 10 && category;

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)
      .slice(0, 5);
    try {
      const { data } = await api.post("/forum/posts", {
        title: title.trim(), body: body.trim(), category, tags,
      });
      if (data.looks_like_deal) {
        // Soft nudge — they confirm or back out.
        const proceed = window.confirm(
          "This looks like it might be a deal listing. Deals belong on the Deal Wall " +
          "(not the Forum). Post anyway, or cancel and head to the Deal Wall?",
        );
        if (!proceed) {
          // Roll back what we created so the forum stays clean.
          try { await api.delete(`/forum/posts/${data.id}`); } catch { /* best-effort */ }
          setSaving(false);
          onOpenChange(false);
          nav("/v3/community");
          return;
        }
      }
      toast.success("Question posted");
      onOpenChange(false);
      nav(`/app/community/forum/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post question");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ask-question-modal">
        <DialogHeader>
          <DialogTitle>Ask the Community</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
            <LinkIcon size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Posting a deal? Use the{" "}
              <button
                type="button"
                className="underline font-semibold"
                onClick={() => { onOpenChange(false); nav("/v3/community"); }}
              >
                Deal Wall
              </button>{" "}
              instead. The Forum is for questions, advice, and discussion.
            </span>
          </div>

          <div>
            <Label htmlFor="forum-title">Title</Label>
            <Input
              id="forum-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Be specific. What's the actual question?"
              maxLength={200}
              data-testid="ask-title-input"
            />
            <div className="text-xs text-zinc-500 mt-1">{title.length}/200 — at least 8 characters</div>
          </div>

          <div>
            <Label>Category</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
              {(categories || []).map((c) => {
                const meta = metaFor(c.id);
                const Icon = meta.icon;
                const selected = category === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={
                      "flex items-center gap-2 rounded-md border px-3 py-2 text-xs text-left transition " +
                      (selected
                        ? "border-zinc-900 bg-zinc-900 text-white"
                        : "border-zinc-200 hover:border-zinc-400 bg-white text-zinc-700")
                    }
                    data-testid={`ask-category-${c.id}`}
                  >
                    <Icon size={14} style={selected ? {} : { color: meta.accent }} />
                    <span className="font-medium">{c.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Question details</Label>
            <MarkdownEditor
              value={body}
              onChange={setBody}
              placeholder="Give context. What have you already tried? What does success look like?"
              rows={8}
              testIdPrefix="ask-body"
            />
            <div className="text-xs text-zinc-500 mt-1">{body.length}/10000 — at least 10 characters</div>
          </div>

          <div>
            <Label htmlFor="forum-tags">Tags (optional)</Label>
            <Input
              id="forum-tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="comma, separated, up-to-5"
              data-testid="ask-tags-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ask-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="ask-submit-btn">
            {saving ? "Posting…" : "Post question"}
          </Button>
        </DialogFooter>
        {!canSubmit && (title || body) && (
          <div className="text-xs text-amber-700 flex items-center gap-1">
            <AlertTriangle size={12} />
            Title needs 8+ chars, body needs 10+, pick a category.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
